import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import api from '../api';
import { useStaff } from '../contexts/StaffContext';
import Spinner from '../components/Spinner';

interface AttendanceRecord {
  _id: string;
  student: { _id: string; name: string };
  className: string;
  attendanceDate: string;
  status: 'present' | 'absent' | 'late' | 'excused';
}

interface StudentStats {
  name: string;
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  excusedDays: number;
  attendancePercentage: number;
}

const StaffAttendance: React.FC = () => {
  const { staff } = useStaff();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState('');
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [students, setStudents] = useState<Array<{ _id: string; name: string; className: string }>>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<'present' | 'absent' | 'late' | 'excused'>('present');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [studentStats, setStudentStats] = useState<StudentStats[]>([]);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);

  // Clear cache when staff member logs out or switches accounts
  const clearAttendanceCache = useCallback(() => {
    setAttendance([]);
    setStudents([]);
    setClasses([]);
    setEditingId(null);
    setError(null);
    setSuccess(null);
    setOpenStatsDialog(false);
    setStudentStats([]);
  }, []);

  // Clear cache when staff context changes (logout or user switch)
  useEffect(() => {
    if (!staff) {
      clearAttendanceCache();
    }
  }, [staff, clearAttendanceCache]);

  const loadClasses = useCallback(async () => {
    try {
      const response = await api.get('/staff/students');
      const uniqueClasses = Array.from(new Set(response.data.map((s: any) => s.className))) as string[];
      setClasses(uniqueClasses);
      if (uniqueClasses.length > 0) {
        setSelectedClass(uniqueClasses[0]);
      }
    } catch {
      setClasses([]);
    }
  }, []);

  const loadStudents = useCallback(async () => {
    try {
      const response = await api.get('/staff/students');
      setStudents(response.data);
    } catch {
      setStudents([]);
    }
  }, []);

  const loadAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/staff/attendance`, {
        params: {
          date: selectedDate,
          className: selectedClass
        }
      });
      setAttendance(response.data);
    } catch (err: any) {
      setError('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedClass]);

  useEffect(() => {
    loadClasses();
    loadStudents();
  }, [loadClasses, loadStudents]);

  useEffect(() => {
    if (selectedClass && selectedDate) {
      loadAttendance();
    }
  }, [selectedClass, selectedDate, loadAttendance]);

  const loadStudentStats = async () => {
    try {
      const response = await api.get('/staff/attendance-stats', {
        params: { className: selectedClass }
      });
      const normalizedStats = response.data.map((stat: any) => ({
        ...stat,
        attendancePercentage: Number(stat.attendancePercentage) || 0
      }));
      setStudentStats(normalizedStats);
      setOpenStatsDialog(true);
    } catch (err: any) {
      setError('Failed to load statistics');
    }
  };

  const classStudents = students.filter((s) => s.className === selectedClass);

  const handleMarkAttendance = async (studentId: string, status: 'present' | 'absent' | 'late' | 'excused') => {
    try {
      setError(null);
      setSuccess(null);

      const existingRecord = attendance.find((a) => a.student?._id === studentId);

      if (existingRecord) {
        await api.put(`/staff/attendance/${existingRecord._id}`, { status });
      } else {
        await api.post('/staff/attendance', {
          studentId,
          date: selectedDate,
          className: selectedClass,
          status
        });
      }

      setSuccess('Attendance marked successfully');
      loadAttendance();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to mark attendance');
    }
  };

  const handleBulkStatus = async (status: 'present' | 'absent' | 'late' | 'excused') => {
    try {
      setError(null);
      setSuccess(null);

      for (const student of classStudents) {
        const existingRecord = attendance.find((a) => a.student?._id === student._id);

        if (existingRecord) {
          await api.put(`/staff/attendance/${existingRecord._id}`, { status });
        } else {
          await api.post('/staff/attendance', {
            studentId: student._id,
            date: selectedDate,
            className: selectedClass,
            status
          });
        }
      }

      setSuccess('All students marked successfully');
      loadAttendance();
    } catch (err: any) {
      setError('Failed to mark attendance');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'present':
        return 'success';
      case 'absent':
        return 'error';
      case 'late':
        return 'warning';
      case 'excused':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStudentStatus = (studentId: string) => {
    const record = attendance.find((a) => a.student?._id === studentId);
    return record?.status || null;
  };

  if (loading && attendance.length === 0) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Daily Attendance</Typography>
        <Button variant="outlined" onClick={loadStudentStats}>
          View Statistics
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          label="Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Class</InputLabel>
          <Select
            value={selectedClass}
            label="Class"
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            {classes.map((cls) => (
              <MenuItem key={cls} value={cls}>
                {cls}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Bulk Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          color="success"
          onClick={() => handleBulkStatus('present')}
        >
          Mark All Present
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => handleBulkStatus('absent')}
        >
          Mark All Absent
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => handleBulkStatus('late')}
        >
          Mark All Late
        </Button>
      </Box>

      {/* Attendance Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {classStudents.length > 0 ? (
              classStudents.map((student) => {
                const currentStatus = getStudentStatus(student._id);
                const isEditing = editingId === student._id;

                return (
                  <TableRow key={student._id}>
                    <TableCell sx={{ color: 'text.primary' }}>{student.name}</TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>
                      <Typography variant="body2" color="textSecondary">
                        {new Date(selectedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>
                      {isEditing ? (
                        <Select
                          value={editingStatus}
                          onChange={(e) => setEditingStatus(e.target.value as any)}
                          size="small"
                        >
                          <MenuItem value="present">Present</MenuItem>
                          <MenuItem value="absent">Absent</MenuItem>
                          <MenuItem value="late">Late</MenuItem>
                          <MenuItem value="excused">Excused</MenuItem>
                        </Select>
                      ) : currentStatus ? (
                        <Chip
                          label={currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
                          color={getStatusColor(currentStatus) as any}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Not marked
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => {
                              handleMarkAttendance(student._id, editingStatus);
                              setEditingId(null);
                            }}
                          >
                            <Save fontSize="small" />
                          </Button>
                          <Button
                            size="small"
                            onClick={() => setEditingId(null)}
                          >
                            <Cancel fontSize="small" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingId(student._id);
                            setEditingStatus(currentStatus || 'present');
                          }}
                        >
                          <Edit fontSize="small" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                  No students in this class
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Statistics Dialog */}
      <Dialog open={openStatsDialog} onClose={() => setOpenStatsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Attendance Statistics - {selectedClass}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Student</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Total Days</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Present</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Absent</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Late</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Excused</TableCell>
                  <TableCell align="center" sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Attendance %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentStats.map((stat, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ color: 'text.primary' }}>{stat.name}</TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>{stat.totalDays}</TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>
                      <Chip label={stat.presentDays} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>
                      <Chip label={stat.absentDays} color="error" size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>
                      <Chip label={stat.lateDays} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>
                      <Chip label={stat.excusedDays} color="info" size="small" />
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.primary' }}>
                      <Typography
                        variant="body2"
                        color={stat.attendancePercentage >= 80 ? 'success.main' : 'error.main'}
                      >
                        {stat.attendancePercentage.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffAttendance;
